import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { submitForm } from "@/api/apis";

interface BusinessLeadFormProps {
  onClose: () => void;
}

export default function BusinessLeadForm({ onClose }: BusinessLeadFormProps) {
  const [, setLocation] = useLocation();
  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    businessType: "Restaurant",
    location: "",
    musicUsage: "",
    currentLicensing: "No",
    consent: false,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) =>
    setData({
      ...data,
      [e.target.name]:
        e.target.type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : e.target.value,
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    /* ---------- Email Form Submission ---------- */
    const emailData = {
      to: "contact@wearemusicnation.ae",
      subject: "New Business Registration",
      body: `Name: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\nCompany: ${data.companyName}\nBusiness Type: ${data.businessType}\nLocation: ${data.location}\nMusic Usage: ${data.musicUsage}\nCurrent Licensing: ${data.currentLicensing}`,
    };

    console.log("Business form sent to:", emailData.to);
    console.log("Business lead captured:", data);

    const API_KEYS: any = {
      name: "contact_name",
      email: "business_email",
      phone: "phone_number",
      companyName: "company_name",
      businessType: "business_type",
      location: "business_location",
      musicUsage: "usage_description",
      currentLicensing: "has_licensing",
      consent: "agree_contact",
    };

    const formData: any = data;

    Object.keys(data).map((key: string) => {
      formData[API_KEYS[key] || key] = formData[key];
      if (API_KEYS[key]) {
        delete formData[key];
      }
    });

    submitForm("BusinessSubmission", data)
      .then((res) => {
        onClose();

        setTimeout(() => {
          setLocation("/thank-you-business");
        }, 100);
      })
      .catch((e) => {
        console.log("SOMETHING WENT WRONG", e);
      });
  };

  return (
    <form
      name="business-lead"
      data-netlify="true"
      netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
      className="space-y-6 p-8 bg-musicBlack rounded-lg max-w-lg mx-auto text-musicWhite"
    >
      <input type="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value="business-lead" />

      <div className="text-center mb-6">
        <h2 className="font-serif text-2xl font-bold mb-2">
          Business Registration
        </h2>
        <p className="font-sans text-musicGrey">
          Get licensed to use music legally
        </p>
      </div>

      {/* Company Name */}
      <div>
        <label className="block text-sm mb-1 font-sans">Business Name *</label>
        <input
          name="companyName"
          type="text"
          required
          placeholder="Your company name"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.companyName}
          onChange={handleChange}
        />
      </div>

      {/* Contact Name */}
      <div>
        <label className="block text-sm mb-1 font-sans">Contact Name *</label>
        <input
          name="name"
          type="text"
          required
          placeholder="Your full name"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.name}
          onChange={handleChange}
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm mb-1 font-sans">Business Email *</label>
        <input
          name="email"
          type="email"
          required
          placeholder="business@company.com"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.email}
          onChange={handleChange}
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm mb-1 font-sans">Phone Number *</label>
        <input
          name="phone"
          type="tel"
          required
          placeholder="+971 XX XXX XXXX"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.phone}
          onChange={handleChange}
        />
      </div>

      {/* Business Type */}
      <div>
        <label className="block text-sm mb-1 font-sans">Business Type *</label>
        <select
          name="businessType"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.businessType}
          onChange={handleChange}
          required
        >
          {[
            "Restaurant/Café",
            "Retail Store",
            "Hotel",
            "Gym/Fitness Center",
            "Bar/Nightclub",
            //"Office Space",
            "Event Venue",
            "Shopping Mall",
            "Spa/Wellness Center",
            "Other",
            "Airline",
            "Cinema",
            "Digital Service Provider (Audio Only)",
            "Digital Service Provider (Audiovisual)",
            "Digital Service Provider (Other)",
            "Broadcast Radio",
            "Broadcast/Cable/Satellite Television",
            "Event Promoter",
            "Hairdresser/Salon",
          ]
            .sort()
            .map((op, i) => (
              <option key={i} value={op}>
                {op}
              </option>
            ))}
        </select>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm mb-1 font-sans">
          Business Address *
        </label>
        <input
          name="location"
          type="text"
          required
          placeholder="City, UAE"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.location}
          onChange={handleChange}
        />
      </div>

      {/* Music Usage */}
      <div>
        <label className="block text-sm mb-1 font-sans">
          How do/will you use music?
        </label>
        <textarea
          name="musicUsage"
          placeholder="Background music, live performances, events, etc."
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 h-20 font-sans"
          value={data.musicUsage}
          onChange={handleChange}
        />
      </div>

      {/* Current Licensing */}
      <div>
        <label className="block text-sm mb-1 font-sans">
          Do you currently have music licensing?
        </label>
        <select
          name="currentLicensing"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3 font-sans"
          value={data.currentLicensing}
          onChange={handleChange}
        >
          <option value="No">No licensing currently</option>
          <option value="Some">Some licensing in place</option>
          <option value="Unsure">Not sure what we have</option>
          <option value="Full">Believe we have full licensing</option>
        </select>
      </div>

      {/* Consent */}
      <label className="flex items-center space-x-2 text-sm font-sans">
        <input
          type="checkbox"
          name="consent"
          checked={data.consent}
          onChange={handleChange}
          required
        />
        <span>
          I agree to be contacted by Music Nation regarding business licensing
          services.
        </span>
      </label>

      <Button type="submit" className="cta-button w-full">
        Submit Business Registration
      </Button>
    </form>
  );
}
